/**
 * Visite interactive « Lecture guidée » — parcourt pas à pas le contenu du guide (AIDE_SECTIONS),
 * bilingue FR/EN, avec navigation clavier (← → Échap), barre de progression et lecture automatique.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import * as Icons from 'lucide-react';
import { AIDE_SECTIONS } from './aideContent';

const resolveIcon = (name) => (name && Icons[name]) || Icons.HelpCircle;

export default function GuideTour({ glang = 'fr', onClose }) {
  const L = (obj) => (obj ? (obj[glang] ?? obj.fr ?? '') : '');

  // Aplatit sections → étapes (une par item).
  const steps = useMemo(() => {
    const out = [];
    AIDE_SECTIONS.forEach((s) => {
      (s.items || []).forEach((it) => out.push({ section: s, item: it }));
    });
    return out;
  }, []);

  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const timer = useRef(null);
  const total = steps.length;

  const go = useCallback((n) => setI((cur) => Math.max(0, Math.min(total - 1, n))), [total]);
  const next = useCallback(() => setI((cur) => (cur >= total - 1 ? cur : cur + 1)), [total]);
  const prev = useCallback(() => go(i - 1), [go, i]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, onClose]);

  useEffect(() => {
    if (!playing) { if (timer.current) clearTimeout(timer.current); return; }
    if (i >= total - 1) { setPlaying(false); return; }
    timer.current = setTimeout(() => setI((c) => c + 1), 4500);
    return () => timer.current && clearTimeout(timer.current);
  }, [playing, i, total]);

  if (!total) return null;
  const { section, item } = steps[i];
  const SIcon = resolveIcon(section.icon);
  const IIcon = resolveIcon(item.icon);
  const stepsList = [].concat(L(item.steps) || []);
  const can = [].concat(L(item.can) || []);
  const cannot = [].concat(L(item.cannot) || []);
  // Chrome de navigation en 7 langues (clé = libellé FR ; repli fr/en pour compat appels).
  const TR = {
    'Visite guidée': { en: 'Guided tour', es: 'Visita guiada', 'pt-BR': 'Visita guiada', ar: 'جولة إرشادية', 'ar-SY': 'جولة إرشادية', zh: '引导浏览' },
    Fermer: { en: 'Close', es: 'Cerrar', 'pt-BR': 'Fechar', ar: 'إغلاق', 'ar-SY': 'سكّر', zh: '关闭' },
    'Où': { en: 'Where', es: 'Dónde', 'pt-BR': 'Onde', ar: 'أين', 'ar-SY': 'وين', zh: '在哪里' },
    'Précédent': { en: 'Previous', es: 'Anterior', 'pt-BR': 'Anterior', ar: 'السابق', 'ar-SY': 'السابق', zh: '上一步' },
    Pause: { en: 'Pause', es: 'Pausa', 'pt-BR': 'Pausar', ar: 'إيقاف مؤقت', 'ar-SY': 'وقف', zh: '暂停' },
    Lecture: { en: 'Play', es: 'Reproducir', 'pt-BR': 'Reproduzir', ar: 'تشغيل', 'ar-SY': 'تشغيل', zh: '播放' },
    Terminer: { en: 'Finish', es: 'Finalizar', 'pt-BR': 'Concluir', ar: 'إنهاء', 'ar-SY': 'خلّص', zh: '完成' },
    Suivant: { en: 'Next', es: 'Siguiente', 'pt-BR': 'Próximo', ar: 'التالي', 'ar-SY': 'التالي', zh: '下一步' },
  };
  const t = (fr, en) => (glang === 'fr' ? fr : (TR[fr] && TR[fr][glang]) || (glang === 'en' ? en : fr));

  return (
    <div role="dialog" aria-modal="true" aria-label={t('Visite guidée', 'Guided tour')} data-testid="aide-tour"
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(2,6,15,0.82)', backdropFilter: 'blur(4px)', display: 'grid', placeItems: 'center', padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={{ width: '100%', maxWidth: 620, background: '#0f1420', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 16, overflow: 'hidden' }}>
        {/* Progression */}
        <div style={{ height: 4, background: 'rgba(255,255,255,0.08)' }}>
          <div style={{ height: '100%', width: `${((i + 1) / total) * 100}%`, background: '#ef4444', transition: 'width .3s' }} />
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            {React.createElement(SIcon, { size: 16, color: '#f87171' })}
            <span style={{ fontSize: 12, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>{L(section.title)}</span>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#9ca3af' }}>{i + 1} / {total}</span>
            <button onClick={onClose} aria-label={t('Fermer', 'Close')} data-testid="aide-tour-close"
              style={{ background: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer' }}>
              {React.createElement(Icons.X, { size: 20 })}
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#111827', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
              {React.createElement(IIcon, { size: 20, color: '#93c5fd' })}
            </div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f9fafb' }}>{L(item.title)}</h3>
          </div>

          {L(item.where) && <p style={{ fontSize: 13, color: '#93c5fd', margin: '0 0 10px' }}><strong>{t('Où', 'Where')} : </strong>{L(item.where)}</p>}

          {stepsList.length > 0 && (
            <ol style={{ margin: '0 0 10px', paddingLeft: 20, color: '#e5e7eb', fontSize: 14, lineHeight: 1.55 }}>
              {stepsList.map((st, k) => <li key={k} style={{ marginBottom: 4 }}>{st}</li>)}
            </ol>
          )}
          {can.length > 0 && (
            <ul style={{ margin: '0 0 6px', paddingLeft: 18, color: '#6ee7b7', fontSize: 13, lineHeight: 1.5 }}>
              {can.map((c, k) => <li key={k}>{c}</li>)}
            </ul>
          )}
          {cannot.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, color: '#fcd34d', fontSize: 13, lineHeight: 1.5 }}>
              {cannot.map((c, k) => <li key={k}>{c}</li>)}
            </ul>
          )}
        </div>

        {/* Contrôles */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 18px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button onClick={prev} disabled={i === 0} data-testid="aide-tour-prev"
            style={ctrl(i === 0)}>{React.createElement(Icons.ChevronLeft, { size: 18 })}<span style={{ marginLeft: 4 }}>{t('Précédent', 'Previous')}</span></button>
          <button onClick={() => setPlaying((p) => !p)} data-testid="aide-tour-play"
            style={{ ...ctrl(false), marginLeft: 'auto' }} aria-label={playing ? t('Pause', 'Pause') : t('Lecture', 'Play')}>
            {React.createElement(playing ? Icons.Pause : Icons.Play, { size: 18 })}
          </button>
          {i >= total - 1 ? (
            <button onClick={onClose} data-testid="aide-tour-finish" style={ctrlPrimary()}>{t('Terminer', 'Finish')}</button>
          ) : (
            <button onClick={next} data-testid="aide-tour-next" style={ctrlPrimary()}>
              <span style={{ marginRight: 4 }}>{t('Suivant', 'Next')}</span>{React.createElement(Icons.ChevronRight, { size: 18 })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const ctrl = (disabled) => ({
  display: 'inline-flex', alignItems: 'center', padding: '8px 12px', borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.14)', background: 'transparent',
  color: disabled ? '#4b5563' : '#e5e7eb', cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 14,
});
const ctrlPrimary = () => ({
  display: 'inline-flex', alignItems: 'center', padding: '8px 14px', borderRadius: 10,
  border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 14,
});
